/**
 * ModuleReader.jsx
 * One reading screen for every MbuziPlus Course module that is written as data
 * (see ./modules). Lessons, a check question in each lesson, a module quiz and a
 * journal. Progress is saved to the student's own record, under the module id.
 */
import { useState, useEffect, useRef } from "react";
import { optionOrder } from "./shuffle";
import { ChevronLeft, ChevronRight, CheckCircle, BookOpen, HelpCircle, PenLine, Circle } from "lucide-react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../Libs/firebase-config.mjs";
import { ModuleIntro, SourcesPanel } from "./ModuleFrame";

const ref = (u) => doc(db, "imfuyo_academy", u.token, "mbuziplus_course", u.pid || u.token);

export default function ModuleReader({ content, isDark, user, onBack }) {
  const token = user?.token ? user : null;
  const [view, setView] = useState("home");
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState({});
  const [answers, setAnswers] = useState({});
  const [journal, setJournal] = useState("");
  const [saveState, setSaveState] = useState("idle");
  const [checked, setChecked] = useState({}); // lessonId -> chosen option
  const timer = useRef(null);

  const c = isDark
    ? { bg: "#0d1a0f", card: "#13231a", line: "#223328", ink: "#f0f7f0", sub: "#a9c1ac", soft: "#16301c" }
    : { bg: "#f5f9f5", card: "#ffffff", line: "#d8e6d8", ink: "#0d1a0f", sub: "#44624a", soft: "#eef6ea" };
  const GREEN = "#3E7A32", ORANGE = "#D9601A";

  const save = (patch) => {
    if (!token) return;
    setDoc(ref(token), { [content.id]: patch, updatedAt: serverTimestamp() }, { merge: true }).catch((e) => console.error("save:", e));
  };

  useEffect(() => {
    if (!token) { setLoading(false); return undefined; }
    const t = setTimeout(() => setLoading(false), 3000); // never leave the student on a blank screen
    getDoc(ref(token)).then((s) => {
      const d = (s.exists() && s.data()[content.id]) || {};
      if (d.completed) setCompleted(d.completed);
      if (d.answers) setAnswers(d.answers);
      if (typeof d.journal === "string") setJournal(d.journal);
    }).catch(() => {}).finally(() => { clearTimeout(t); setLoading(false); });
    return () => clearTimeout(t);
  }, [token, content.id]);

  const markDone = (id) => setCompleted((p) => {
    if (p[id]) return p;
    const n = { ...p, [id]: true };
    save({ completed: n });
    return n;
  });

  const items = [
    ...content.lessons.map((l) => ({ id: l.id, type: "lesson", title: l.title, meta: `${l.minutes} min`, Icon: BookOpen })),
    { id: "quiz", type: "quiz", title: "Module quiz", meta: `${content.quiz.length} questions`, Icon: HelpCircle },
    { id: "journal", type: "journal", title: content.journalTitle, meta: "Write and save", Icon: PenLine },
  ];
  const doneCount = items.filter((i) => completed[i.id]).length;
  const pct = Math.round((doneCount / items.length) * 100);

  const open = (id) => { setView(id); if (id !== "quiz" && id !== "journal") markDone(id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const next = () => {
    const i = items.findIndex((x) => x.id === view);
    if (i >= 0 && i < items.length - 1) open(items[i + 1].id); else { setView("home"); window.scrollTo({ top: 0 }); }
  };

  const choose = (qi, oi) => {
    if (answers[qi] !== undefined) return;
    const n = { ...answers, [qi]: oi };
    setAnswers(n);
    const all = Object.keys(n).length === content.quiz.length;
    const comp = all ? { ...completed, quiz: true } : completed;
    if (all) setCompleted(comp);
    save({ answers: n, completed: comp });
  };
  const correctCount = Object.entries(answers).filter(([qi, a]) => content.quiz[+qi].correct === a).length;

  const onJournal = (v) => {
    setJournal(v); setSaveState("idle");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setSaveState("saving");
      const words = v.trim().split(/\s+/).filter(Boolean).length;
      const comp = words >= 5 ? { ...completed, journal: true } : completed;
      if (words >= 5 && !completed.journal) setCompleted(comp);
      save({ journal: v, completed: comp });
      setSaveState("saved");
    }, 900);
  };

  const wrap = { minHeight: "100vh", background: c.bg, color: c.ink, fontFamily: "'Inter', system-ui, sans-serif", paddingTop: 80 };
  const col = { maxWidth: 760, margin: "0 auto", padding: "0 max(18px, env(safe-area-inset-left)) 90px" };
  const btn = { display: "inline-flex", alignItems: "center", gap: 8, border: "none", borderRadius: 999, padding: "14px 26px", fontSize: 16, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" };

  const TopBar = ({ label, onClick }) => (
    <div style={{ ...col, paddingBottom: 0, paddingTop: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <button onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${c.line}`, borderRadius: 999, padding: "9px 16px", fontSize: 14, fontWeight: 700, color: c.sub, cursor: "pointer", fontFamily: "inherit" }}><ChevronLeft size={15} /> {label}</button>
      <span style={{ fontSize: 14, fontWeight: 700, color: c.sub }}>{doneCount} of {items.length} done</span>
    </div>
  );

  const Block = ({ b }) => {
    if (b.t === "p") return <p style={{ margin: "0 0 18px", fontSize: 18, lineHeight: 1.75 }}>{b.x}</p>;
    if (b.t === "h") return <h3 style={{ margin: "30px 0 10px", fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em" }}>{b.x}</h3>;
    if (b.t === "ul") return <ul style={{ margin: "0 0 20px", paddingLeft: 24, fontSize: 18, lineHeight: 1.75 }}>{b.items.map((i) => <li key={i} style={{ marginBottom: 6 }}>{i}</li>)}</ul>;
    if (b.t === "callout") return <div style={{ margin: "8px 0 22px", padding: "16px 18px", borderRadius: 14, background: c.soft, borderLeft: `5px solid ${GREEN}`, fontSize: 17, lineHeight: 1.65 }}>{b.x}</div>;
    if (b.t === "table") return (
      <div style={{ overflowX: "auto", margin: "4px 0 22px", border: `1px solid ${c.line}`, borderRadius: 14 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 16, lineHeight: 1.5 }}>
          <thead><tr>{b.head.map((h) => <th key={h} style={{ textAlign: "left", padding: "12px 14px", background: GREEN, color: "#fff", fontWeight: 800, whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
          <tbody>{b.rows.map((r, i) => <tr key={i} style={{ background: i % 2 ? c.soft : c.card }}>{r.map((x, j) => <td key={j} style={{ padding: "12px 14px", fontWeight: j === 0 || r[0] === "Package total" ? 700 : 400, borderTop: `1px solid ${c.line}`, verticalAlign: "top" }}>{x}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
    return null;
  };

  const Option = ({ text, state, onClick, disabled }) => {
    const bgc = state === "right" ? (isDark ? "#14301A" : "#e3f1dc") : state === "wrong" ? (isDark ? "#3a1a1a" : "#fdeaea") : c.card;
    const bd = state === "right" ? GREEN : state === "wrong" ? "#d14343" : c.line;
    return <button onClick={onClick} disabled={disabled} style={{ display: "block", width: "100%", textAlign: "left", padding: "14px 16px", marginBottom: 8, borderRadius: 12, border: `2px solid ${bd}`, background: bgc, color: c.ink, fontSize: 16.5, lineHeight: 1.45, cursor: disabled ? "default" : "pointer", fontFamily: "inherit" }}>{text}</button>;
  };
  const stateOf = (chosen, i, correct) => chosen === undefined ? "" : i === correct ? "right" : i === chosen ? "wrong" : "";

  if (loading) return <div style={{ ...wrap, display: "grid", placeItems: "center" }}>Loading your module…</div>;

  /* ── HOME ── */
  if (view === "home") return (
    <div style={wrap}>
      <TopBar label="Course" onClick={onBack} />
      <main style={col}>
        <div style={{ marginTop: 18 }}><ModuleIntro moduleId={content.id} isDark={isDark} onStart={() => open(items[0].id)} /></div>
        <div style={{ height: 10, borderRadius: 6, background: c.line, overflow: "hidden", margin: "6px 0 8px" }}><div style={{ width: `${pct}%`, height: "100%", background: GREEN, transition: "width .5s" }} /></div>
        <div style={{ fontSize: 15, fontWeight: 700, color: c.sub, marginBottom: 18 }}>{pct}% of this module done</div>
        <div style={{ display: "grid", gap: 10 }}>
          {items.map((it, i) => (
            <button key={it.id} onClick={() => open(it.id)} style={{ display: "flex", alignItems: "center", gap: 14, textAlign: "left", background: c.card, border: `1px solid ${completed[it.id] ? GREEN : c.line}`, borderRadius: 16, padding: "16px 18px", cursor: "pointer", color: c.ink, fontFamily: "inherit" }}>
              {completed[it.id] ? <CheckCircle size={26} color={GREEN} /> : <Circle size={26} color={c.sub} />}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 800, color: c.sub, letterSpacing: "0.06em", textTransform: "uppercase" }}>{it.type === "lesson" ? `Lesson ${i + 1}` : it.type === "quiz" ? "Quiz" : "Journal"}</span>
                <span style={{ display: "block", fontSize: 18, fontWeight: 700, marginTop: 2 }}>{it.title}</span>
              </span>
              <span style={{ fontSize: 14.5, color: c.sub, whiteSpace: "nowrap" }}>{it.meta}</span>
            </button>
          ))}
        </div>
        <SourcesPanel moduleId={content.id} isDark={isDark} />
      </main>
    </div>
  );

  const idx = items.findIndex((x) => x.id === view);
  const nextLabel = idx === items.length - 1 ? "Finish" : "Next";
  const NextBtn = () => <button onClick={next} style={{ ...btn, background: ORANGE, color: "#fff" }}>{nextLabel} <ChevronRight size={18} /></button>;
  const Progress = () => <div style={{ fontSize: 14.5, fontWeight: 700, color: c.sub, margin: "20px 0 6px" }}>{idx + 1} of {items.length}<div style={{ height: 6, borderRadius: 4, background: c.line, marginTop: 8 }}><div style={{ width: `${((idx + 1) / items.length) * 100}%`, height: "100%", background: GREEN, borderRadius: 4 }} /></div></div>;

  /* ── QUIZ ── */
  if (view === "quiz") return (
    <div style={wrap}>
      <TopBar label="Module" onClick={() => setView("home")} />
      <main style={col}>
        <Progress />
        <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", margin: "10px 0 8px" }}>Module quiz</h1>
        <p style={{ fontSize: 17, color: c.sub, lineHeight: 1.6, margin: "0 0 22px" }}>Tap an answer to see why it is right or wrong. Your answers are saved.{Object.keys(answers).length > 0 && ` Score so far: ${correctCount} of ${Object.keys(answers).length}.`}</p>
        {content.quiz.map((q, qi) => (
          <div key={qi} style={{ background: c.card, border: `1px solid ${c.line}`, borderRadius: 18, padding: "20px 20px 14px", marginBottom: 16 }}>
            <p style={{ margin: "0 0 14px", fontSize: 18, fontWeight: 700, lineHeight: 1.5 }}>{qi + 1}. {q.q}</p>
            {optionOrder(`${content.id}:quiz:${qi}`, q.options.length).map((oi) => <Option key={oi} text={q.options[oi]} state={stateOf(answers[qi], oi, q.correct)} disabled={answers[qi] !== undefined} onClick={() => choose(qi, oi)} />)}
            {answers[qi] !== undefined && <p style={{ margin: "6px 0 8px", fontSize: 16, lineHeight: 1.6, color: c.sub }}><b style={{ color: c.ink }}>{answers[qi] === q.correct ? "Correct. " : "Not quite. "}</b>{q.why}</p>}
          </div>
        ))}
        <NextBtn />
      </main>
    </div>
  );

  /* ── JOURNAL ── */
  if (view === "journal") return (
    <div style={wrap}>
      <TopBar label="Module" onClick={() => setView("home")} />
      <main style={col}>
        <Progress />
        <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", margin: "10px 0 8px" }}>{content.journalTitle}</h1>
        <p style={{ fontSize: 18, color: c.sub, lineHeight: 1.65, margin: "0 0 16px" }}>{content.journalPrompt}</p>
        <textarea value={journal} onChange={(e) => onJournal(e.target.value)} rows={10} style={{ width: "100%", boxSizing: "border-box", padding: 16, fontSize: 17, lineHeight: 1.6, borderRadius: 14, border: `2px solid ${c.line}`, background: c.card, color: c.ink, fontFamily: "inherit", resize: "vertical" }} placeholder="Start writing here…" />
        <div style={{ fontSize: 14.5, color: c.sub, margin: "8px 0 22px" }}>{saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : "Your notes save automatically."}</div>
        <NextBtn />
      </main>
    </div>
  );

  /* ── LESSON ── */
  const L = content.lessons.find((l) => l.id === view);
  const chosen = checked[L.id];
  return (
    <div style={wrap}>
      <TopBar label="Module" onClick={() => setView("home")} />
      <main style={col} data-mbz-lesson>
        <Progress />
        <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: ORANGE, marginTop: 14 }}>Lesson {idx + 1} · {L.minutes} min</div>
        <h1 style={{ fontSize: "clamp(28px, 5vw, 38px)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.15, margin: "6px 0 22px" }}>{L.title}</h1>
        {L.blocks.map((b, i) => <Block key={i} b={b} />)}
        <div style={{ background: c.card, border: `2px solid ${c.line}`, borderRadius: 18, padding: "20px 20px 12px", margin: "30px 0 26px" }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: GREEN, marginBottom: 6 }}>Check yourself</div>
          <p style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700, lineHeight: 1.5 }}>{L.check.q}</p>
          {optionOrder(`${content.id}:check:${L.id}`, L.check.options.length).map((oi) => <Option key={oi} text={L.check.options[oi]} state={stateOf(chosen, oi, L.check.correct)} disabled={chosen !== undefined} onClick={() => setChecked((p) => ({ ...p, [L.id]: oi }))} />)}
          {chosen !== undefined && <p style={{ margin: "6px 0 8px", fontSize: 16, lineHeight: 1.6, color: c.sub }}><b style={{ color: c.ink }}>{chosen === L.check.correct ? "Correct. " : "Not quite. "}</b>{L.check.why}</p>}
        </div>
        <NextBtn />
      </main>
    </div>
  );
}
